/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { View, Text, Actionable } from '@az/starc-ui';
import MobileBlogMenuNavigationStyles from './MobileBlogMenuNavigation.module.scss';
import ExpandableSecondaryLinkStyles from './ExpandableSecondaryLink.module.scss';
import { MobileBlogMenuModal } from './Modal/MobileMenuModal';
import { useState } from 'react';
import { useLabels } from '@/hooks/useLabels';
import { useContentStackBlogMenuNavigationData } from '../../api/getContentStackBlogMenuNavigationData';
import { MobileBlogMenuButton } from './Modal/MobileMenuButton';
import { FeaturedSection } from './Modal/FeaturedSection';
import { ExpandableSecondaryLink } from './ExpandableSecondaryLink';
import { CMSLink } from '@/features/contentstack/components/CMSLink/CMSLink';

const labelMap = {
  mobileMenuButton: 'label_BLOG_MobileMenuButton',
  featuredSectionHeading: 'label_BLOG_FeaturedSectionHeading',
};

export const MobileBlogMenuNavigation = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const labels = useLabels(labelMap);
  const { data: menuData } = useContentStackBlogMenuNavigationData();
  const menuNavigation = menuData?.menu_navigation ?? [];

  const mainLinks = menuNavigation.at(0);
  const secondaryLinks = menuNavigation.slice(1).reverse();

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <MobileBlogMenuButton handleOpenModal={handleOpenModal} />
      <MobileBlogMenuModal
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        title={labels.mobileMenuButton}
      >
        <View direction="column">
          {secondaryLinks.length > 0 && (
            <View
              direction="column"
              className={MobileBlogMenuNavigationStyles.secondaryLinksContainer}
              attributes={{ 'data-testid': 'secondary-links' }}
            >
              {secondaryLinks?.map((section) => (
                <ExpandableSecondaryLink
                  key={section.menu_heading}
                  section={section}
                  className={ExpandableSecondaryLinkStyles.secondaryLinks}
                  handleCloseModal={handleCloseModal}
                />
              ))}
            </View>
          )}
          {mainLinks && (
            <View direction="column">
              <View direction="row" justify="space-between" align="center">
                <Text weight="bold" className={MobileBlogMenuNavigationStyles.mainSectionHeading}>
                  {mainLinks.menu_heading}
                </Text>

                {mainLinks.section_cta?.href ? (
                  <Actionable href={mainLinks.section_cta.href}>
                    <Text
                      decoration="underline"
                      size="087"
                      weight="medium"
                      className={MobileBlogMenuNavigationStyles.topLink}
                    >
                      {mainLinks.section_cta.title}
                    </Text>
                  </Actionable>
                ) : null}
              </View>
              <View direction="column" className={MobileBlogMenuNavigationStyles.mainSection}>
                <View
                  direction="column"
                  attributes={{ 'data-testid': 'main-links' }}
                  className={MobileBlogMenuNavigationStyles.mainSectionLinks}
                >
                  {mainLinks.menu_link_group?.map((linkItem) => {
                    if (!linkItem.menu_link?.href) {
                      return null;
                    }

                    return (
                      <CMSLink
                        key={linkItem.menu_link?.title}
                        to={linkItem.menu_link?.href}
                        onClick={handleCloseModal}
                      >
                        <Text
                          weight="regular"
                          size="087"
                          className={MobileBlogMenuNavigationStyles.link}
                        >
                          {linkItem.menu_link?.title}
                        </Text>
                      </CMSLink>
                    );
                  })}
                </View>
                <View className={MobileBlogMenuNavigationStyles.divider} />
                <FeaturedSection
                  featuredLinkContent={mainLinks.feature_link_content}
                  handleCloseModal={handleCloseModal}
                />
              </View>
            </View>
          )}
        </View>
      </MobileBlogMenuModal>
    </>
  );
};
